/**
 * @deprecated - prefer `toLabelCase()` instead for advanced text formatting cases
 */
export function titleCase(val: string): string {
  const words: string[] = val.toLowerCase().split(" ");
  return words
    .map((word: string): string => {
      // the string, word becomes Word so each "word" is capitalized.
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(" ");
}

/**
 * Options for the sentence case converter.
 */
export interface LabelCaseOptions {
  /** Exact strings to ignore (case-sensitive or insensitive depending on logic, usually exact match). */
  excludeStrings?: string[];
  /** Regex patterns to ignore. Matches will be skipped entirely. */
  excludePatterns?: RegExp[];
}

interface LabelToken {
  start: number;
  end: number;
  text: string;
  preserve: boolean;
}

/**
 * Note: This function is provided as temporary means to handling edge cases when backend APIs do not provide proper cased text.
 * The reality is the backend may only provide a simple string, and we need to apply proper cased text for UX compliance whether it is sentence case or predicate case.
 *
 * General utility converts a string to correct text case while preserving explicitly ignored strings or regex patterns to preserve proper nouns and titles.
 *
 * @param text - The input string to convert.
 * @param options - Configuration for excluding specific words or patterns.
 * @param isSentenceCase - Whether to capitalize the very first letter of the sentence. (default: true)
 * @returns A sentence- or predicate-cased string, preserving capitalization rules when necessary for UX compliance.
 */
export function toLabelCase(
  text: string,
  options: LabelCaseOptions = {},
  isSentenceCase = true
): string {
  if (!text) return "";

  const { excludeStrings = [], excludePatterns = [] } = options;

  // 1. Build a master array of all regions that must be skipped/ignored
  const tokens: Array<LabelToken> = [];

  // Find all regex pattern matches
  for (const pattern of excludePatterns) {
    // Ensure the regex has the global flag to find all matches, without mutating the original
    const globalPattern = pattern.global
      ? pattern
      : new RegExp(pattern.source, pattern.flags + "g");
    let match;

    // Reset lastIndex just in case
    globalPattern.lastIndex = 0;

    while ((match = globalPattern.exec(text)) !== null) {
      if (match[0].length > 0) {
        tokens.push({
          start: match.index,
          end: match.index + match[0].length,
          text: match[0],
          preserve: true
        });
      }
      // Prevent infinite loops on zero-width matches
      if (match.index === globalPattern.lastIndex) {
        globalPattern.lastIndex++;
      }
    }
  }

  // Find all exact string matches
  for (const excludeStr of excludeStrings) {
    if (!excludeStr) continue;
    let index = text.indexOf(excludeStr);
    while (index !== -1) {
      tokens.push({
        start: index,
        end: index + excludeStr.length,
        text: excludeStr,
        preserve: true
      });
      index = text.indexOf(excludeStr, index + excludeStr.length);
    }
  }

  // 2. Sort exclusions by start index and merge overlapping/adjacent blocks
  tokens.sort((a, b) => a.start - b.start);

  const mergedExclusions: typeof tokens = [];
  for (const current of tokens) {
    if (mergedExclusions.length === 0) {
      mergedExclusions.push(current);
    } else {
      const last: LabelToken | undefined =
        mergedExclusions[mergedExclusions.length - 1];

      if (last && current.start < last.end) {
        // Overlap: extend the boundary if the current one reaches further
        if (current.end > last.end) {
          last.end = current.end;
          last.text = text.slice(last.start, last.end);
        }
      } else {
        mergedExclusions.push(current);
      }
    }
  }

  // 3. Fill in the gaps with the remaining "normal" text blocks
  const allBlocks: typeof tokens = [];
  let lastIdx = 0;

  for (const exclusion of mergedExclusions) {
    if (exclusion.start > lastIdx) {
      allBlocks.push({
        start: lastIdx,
        end: exclusion.start,
        text: text.slice(lastIdx, exclusion.start),
        preserve: false
      });
    }
    allBlocks.push(exclusion);
    lastIdx = exclusion.end;
  }

  if (lastIdx < text.length) {
    allBlocks.push({
      start: lastIdx,
      end: text.length,
      text: text.slice(lastIdx),
      preserve: false
    });
  }

  // 4. Process the blocks into sentence case
  let isFirstCharacterOfSentence = true;
  let result = "";

  for (const block of allBlocks) {
    if (block.preserve) {
      // Keep ignored strings exactly as they are
      result += block.text;
      // If the preserved text contains sentence-ending punctuation,
      // the next normal character should be capitalized.
      if (/[.!?]\s*$/.test(block.text)) {
        isFirstCharacterOfSentence = true;
      } else if (/\S/.test(block.text)) {
        // If it's just non-whitespace text, it counts as content,
        // so upcoming text is no longer the start of the sentence.
        isFirstCharacterOfSentence = false;
      }
    } else {
      // Process standard text down to sentence case
      const blockText = block.text;
      let processedBlock = "";

      for (let i = 0; i < blockText.length; i++) {
        const char = blockText[i];

        if (char) {
          if (isFirstCharacterOfSentence && /\p{L}/u.test(char)) {
            // Capitalize the very first letter of the sentence when isSentenceCase is true
            processedBlock += isSentenceCase
              ? char.toUpperCase()
              : char.toLowerCase();
            isFirstCharacterOfSentence = false;
          } else {
            // Lowercase subsequent letters unless they follow a sentence terminator
            processedBlock += char.toLowerCase();
          }

          // Check if this character terminates a sentence
          if (/[.!?]/.test(char)) {
            isFirstCharacterOfSentence = true;
          }
        }
      }
      result += processedBlock;
    }
  }

  return result;
}

/**
 * Break the string up into an array of constituent parts, typically separated by a comma ( and maybe some optional whitespace )
 */
const countOccurrence = (value: string, separatorToken = /,\s*/) => {
  return value.split(separatorToken).length;
};

/**
 * When the field contains more than the permitted number of entries, prevent additional typing,
 * unless it is to remove text or navigate with the four arrow keys.
 */
export const handleMaxOccurrenceLimit = (
  e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
  maxOccurrence: number
) => {
  const count = countOccurrence(e.currentTarget.value);
  if (
    count > maxOccurrence &&
    e.key !== "Home" &&
    e.key !== "End" &&
    e.key !== "Delete" &&
    e.key !== "Backspace" &&
    e.key !== "ArrowUp" &&
    e.key !== "ArrowDown" &&
    e.key !== "ArrowLeft" &&
    e.key !== "ArrowRight"
  ) {
    e.preventDefault();
  }
};

/**
 * Converts a string from camelCase to snake_case
 * @param inputString
 *
 * example: "helloWorld" => "hello_world"
 * example: "ipv4Addr" => "ipv4_addr"
 */
export function camelToSnakeCase(inputString: string): string {
  return inputString.replace(/([a-z0-9])([A-Z])/g, "$1_$2").toLowerCase();
}

/**
 * Converts a string from snake_case to Display Label
 * @param inputString
 *
 * example: "hello_world" => "Hello world"
 */
export function snakeCaseToLabel(inputString: string): string {
  return titleCase(inputString.replaceAll("_", " "));
}

export function autocorrectString(str: string) {
  // Remove any characters that are not alphanumeric or hyphens
  return str.replace(/[^a-zA-Z0-9-]*/g, "");
}

export const kebabCase = (id: string) => {
  return id.toLowerCase().trim().replaceAll(" ", "-");
};

/**
 * Returns an appropriate string based on the count value, supporting distinct messages for zero, one, and multiple items.
 * Useful for generating grammatically correct labels and messages that change based on quantity.
 *
 * @param count - The numeric count that determines which string to return. Defaults to 0 if not provided.
 * @param single - The string to return when count is exactly 1 (e.g., "1 item", "One device").
 * @param plural - The string to return when count is 2 or greater (e.g., "N items", "Multiple devices").
 * @param none - Optional. The string to return when count is exactly 0 (e.g., "No items", "Nothing selected").
 *              Defaults to the plural string if not provided.
 *
 * @returns The appropriate string based on the count value.
 *
 * @example
 * // Basic usage - most common pattern where zero case uses plural form
 * pluralize(0, "1 item", "5 items")  // Returns "5 items"
 * pluralize(1, "1 item", "5 items")  // Returns "1 item"
 * pluralize(5, "1 item", "5 items")  // Returns "5 items"
 *
 * @example
 * // With explicit zero message
 * pluralize(0, "1 item", "5 items", "No items")  // Returns "No items"
 * pluralize(1, "1 item", "5 items", "No items")  // Returns "1 item"
 * pluralize(5, "1 item", "5 items", "No items")  // Returns "5 items"
 *
 * @example
 * // Dynamic message generation
 * const count = devices.length;
 * const message = pluralize(
 *   count,
 *   "1 device selected",
 *   `${count} devices selected`,
 *   "No devices available"
 * );
 *
 * @example
 * // Conditional UI rendering
 * <Text>
 *   {pluralize(errorCount, "1 error found", `${errorCount} errors found`, "No errors")}
 * </Text>
 *
 * @example
 * // Simple case without zero handling (common for counts that are always > 0)
 * const label = pluralize(selectedItems.length, "item", "items");
 *
 * @remarks
 * - The `none` parameter is optional and defaults to the `plural` value. This is useful for simple cases
 *   like "0 items" where the plural form is grammatically correct for zero.
 * - Negative numbers are treated as plural (default case).
 * - For languages with complex plural rules (e.g., Slavic languages with different forms for 2-4 vs 5+),
 *   consider using a dedicated i18n library like `i18next` or `Intl.PluralRules`.
 * - The function performs strict equality checks (===), so fractional values like 1.5 will use the plural form.
 */
export const pluralize = (
  count: number = 0,
  single: string,
  plural: string,
  none: string = plural
) => {
  switch (count) {
    case 0:
      return none;
    case 1:
      return single;
    default:
      return plural;
  }
};

/**
 * Creates a composite key for map lookups by joining values with a separator.
 * Default separator is tilde (~) to match existing codebase conventions.
 *
 * Returns undefined if any value is empty/undefined/null to prevent:
 * - Key collisions from partial keys
 * - Invalid map entries with undefined as key
 *
 * @param values - Values to join into a composite key
 * @param separator - Separator string (defaults to "~")
 * @returns Composite key string, or undefined if any value is empty
 */
export const createCompositeKey = (
  values: (string | undefined | null)[],
  separator: string = "~"
): string | undefined => {
  if (values.some((v) => !v)) {
    return undefined;
  }
  return values.join(separator);
};

/**
 * Extracts all digits from a string, removing all non-digit characters.
 *
 * @param str - The input string to extract digits from
 * @returns A string containing only the digits from the input
 *
 * @example
 * extractDigits("400G") // Returns "400"
 * extractDigits("abc123def456") // Returns "123456"
 */
export const extractDigits = (str: string): string => {
  return str.replace(/\D/g, "");
};
